<template>
  <modal
    elem-id="soProductImageGalleryModal"
    :show="isVisible"
    :title="t('Galeria de Imagens do Produto')"
    size="large"
    :close-label="t('Fechar')"
    @close="handleClose"
    @dismiss="handleClose"
  >
    <template v-if="isVisible">
      <div class="product-image-gallery" data-testid="product-image-gallery-modal">
        <!-- Upload row -->
        <div class="upload-row">
          <input
            ref="fileInputRef"
            type="file"
            accept="image/*"
            multiple
            class="file-input-hidden"
            id="product-image-upload-input"
            data-testid="product-image-upload-input"
            :disabled="isUploading"
            @change="handleFileSelect"
          />
          <label
            for="product-image-upload-input"
            class="upload-button-label"
            :class="{ 'is-disabled': isUploading }"
            data-testid="product-image-upload-button"
          >
            <i :class="isUploading ? 'pi pi-spin pi-spinner' : 'pi pi-upload'"></i>
            <span>{{ isUploading ? t('Enviando...') : t('Enviar imagens') }}</span>
          </label>
          <span v-if="isUploading" class="upload-progress">
            {{ t('Comprimindo e enviando') }} ({{ uploadProgressCurrent }}/{{ uploadProgressTotal }})
          </span>
        </div>

        <!-- Loading state -->
        <div v-if="isLoading && images.length === 0" class="state-block">
          <i class="pi pi-spin pi-spinner"></i>
          <p>{{ t('Carregando imagens...') }}</p>
        </div>

        <!-- Empty state -->
        <div
          v-else-if="!isLoading && images.length === 0"
          class="state-block empty-state"
          data-testid="product-image-empty-state"
        >
          <i class="pi pi-image"></i>
          <p>{{ t('Nenhuma imagem ainda. Faça upload da primeira.') }}</p>
        </div>

        <!-- Image grid -->
        <div v-else class="image-grid">
          <div
            v-for="image in images"
            :key="image.so_product_image_id"
            class="image-card"
            :class="{ 'is-primary': image.so_product_image_is_primary }"
            :data-testid="`product-image-thumbnail-${image.so_product_image_id}`"
          >
            <div class="image-thumb" @click="openLightbox(image)">
              <img
                :src="image.so_product_image_thumbnail_url"
                :alt="image.so_product_image_alt_text || t('Imagem do produto')"
                loading="lazy"
              />
              <div
                v-if="image.so_product_image_is_primary"
                class="primary-badge"
                :data-testid="`product-image-primary-badge-${image.so_product_image_id}`"
                :title="t('Imagem principal')"
              >
                <i class="pi pi-star-fill"></i>
              </div>
            </div>
            <div class="image-actions">
              <Button
                v-if="!image.so_product_image_is_primary"
                icon="pi pi-star"
                severity="warn"
                size="small"
                outlined
                :label="t('Definir como principal')"
                :data-testid="`product-image-set-primary-btn-${image.so_product_image_id}`"
                :disabled="actingImageId === image.so_product_image_id"
                @click="handleSetPrimary(image.so_product_image_id)"
              />
              <Button
                icon="pi pi-trash"
                severity="danger"
                size="small"
                outlined
                :label="t('Remover')"
                :data-testid="`product-image-delete-btn-${image.so_product_image_id}`"
                :disabled="actingImageId === image.so_product_image_id"
                @click="handleDelete(image.so_product_image_id)"
              />
            </div>
          </div>
        </div>

        <!-- Lightbox overlay -->
        <div
          v-if="lightboxImage"
          class="lightbox-overlay"
          data-testid="product-image-lightbox"
          @click.self="closeLightbox"
        >
          <button
            type="button"
            class="lightbox-close-btn"
            data-testid="product-image-lightbox-close"
            @click="closeLightbox"
          >
            <i class="pi pi-times"></i>
          </button>
          <img
            :src="lightboxImage.so_product_image_url"
            :alt="lightboxImage.so_product_image_alt_text || t('Imagem do produto')"
          />
        </div>
      </div>
    </template>
  </modal>
</template>

<script setup lang="ts">
import { ref, onMounted, onBeforeUnmount } from 'vue';
import { useI18n } from 'vue-i18n';
import { eventBus } from '../../eventBus';
import { log, error as logError } from '../../utils/DebugLogger';
import { useSOProducts } from '../../composables/useSOProducts';
import { useConfirmAction } from '../../composables/useConfirmAction';
import type { Products_ListImageRow } from '../../contracts/Products_ListImageRow';

interface OpenPayload {
  productId: number;
}

const { t } = useI18n();
const {
  uploadProductImage,
  fetchProductImages,
  deleteProductImage,
  setPrimaryProductImage
} = useSOProducts();
const { confirmAction } = useConfirmAction();

const isVisible = ref<boolean>(false);
const isLoading = ref<boolean>(false);
const isUploading = ref<boolean>(false);
const uploadProgressCurrent = ref<number>(0);
const uploadProgressTotal = ref<number>(0);
const actingImageId = ref<number | null>(null);
const productId = ref<number | null>(null);
const images = ref<Products_ListImageRow[]>([]);
const lightboxImage = ref<Products_ListImageRow | null>(null);
const fileInputRef = ref<HTMLInputElement | null>(null);

const loadImages = async (): Promise<void> => {
  if (productId.value === null) return;
  isLoading.value = true;
  try {
    const list = await fetchProductImages(productId.value);
    images.value = [...list].sort(
      (a, b) => a.so_product_image_sort_order - b.so_product_image_sort_order
    );
  } catch (err) {
    logError('SOProductImageGalleryModal', 'loadImages', 'Error', err);
    eventBus.emit('globalMessage.error', t('Erro ao carregar imagens'));
  } finally {
    isLoading.value = false;
  }
};

const handleFileSelect = async (event: Event): Promise<void> => {
  const input = event.target as HTMLInputElement;
  const files = input.files;
  if (!files || files.length === 0 || productId.value === null) return;

  const fileList = Array.from(files);
  isUploading.value = true;
  uploadProgressTotal.value = fileList.length;
  uploadProgressCurrent.value = 0;

  for (const file of fileList) {
    uploadProgressCurrent.value += 1;
    if (!file.type.startsWith('image/')) {
      eventBus.emit(
        'globalMessage.error',
        t('Arquivo inválido') + `: ${file.name}`
      );
      continue;
    }
    try {
      log('SOProductImageGalleryModal', 'handleFileSelect', 'Uploading', {
        name: file.name,
        size: file.size
      });
      await uploadProductImage(productId.value, file);
    } catch (err) {
      logError('SOProductImageGalleryModal', 'handleFileSelect', 'Upload failed', err);
      eventBus.emit(
        'globalMessage.error',
        t('Erro ao enviar imagem') + `: ${file.name}`
      );
    }
  }

  isUploading.value = false;
  uploadProgressCurrent.value = 0;
  uploadProgressTotal.value = 0;
  if (fileInputRef.value) {
    fileInputRef.value.value = '';
  }
  await loadImages();
  eventBus.emit('so-stock.image-uploaded', { productId: productId.value });
};

const handleSetPrimary = (imageId: number): void => {
  confirmAction({
    message: t('Definir esta imagem como principal do produto?'),
    header: t('Confirmação'),
    onAccept: async () => {
      actingImageId.value = imageId;
      try {
        await setPrimaryProductImage(imageId);
        eventBus.emit('globalMessage.success', t('Imagem principal atualizada'));
        await loadImages();
        eventBus.emit('so-stock.image-primary-changed', {
          productId: productId.value,
          imageId
        });
      } catch (err) {
        logError('SOProductImageGalleryModal', 'handleSetPrimary', 'Error', err);
        eventBus.emit('globalMessage.error', t('Erro ao definir imagem principal'));
      } finally {
        actingImageId.value = null;
      }
    }
  });
};

const handleDelete = (imageId: number): void => {
  confirmAction({
    message: t('Tem certeza que deseja remover esta imagem?'),
    header: t('Confirmação'),
    onAccept: async () => {
      actingImageId.value = imageId;
      try {
        await deleteProductImage(imageId);
        eventBus.emit('globalMessage.success', t('Imagem removida'));
        await loadImages();
        eventBus.emit('so-stock.image-deleted', {
          productId: productId.value,
          imageId
        });
      } catch (err) {
        logError('SOProductImageGalleryModal', 'handleDelete', 'Error', err);
        eventBus.emit('globalMessage.error', t('Erro ao remover imagem'));
      } finally {
        actingImageId.value = null;
      }
    }
  });
};

const openLightbox = (image: Products_ListImageRow): void => {
  lightboxImage.value = image;
};

const closeLightbox = (): void => {
  lightboxImage.value = null;
};

const openModal = (payload?: OpenPayload): void => {
  if (!payload || typeof payload.productId !== 'number') {
    logError(
      'SOProductImageGalleryModal',
      'openModal',
      'Missing productId in open payload',
      payload
    );
    return;
  }
  productId.value = payload.productId;
  images.value = [];
  isVisible.value = true;
  loadImages();
};

const handleClose = (): void => {
  isVisible.value = false;
  productId.value = null;
  images.value = [];
  lightboxImage.value = null;
  isUploading.value = false;
  uploadProgressCurrent.value = 0;
  uploadProgressTotal.value = 0;
};

onMounted(() => {
  eventBus.on('modal.so-stock-image-gallery.open', openModal);
});

onBeforeUnmount(() => {
  eventBus.off('modal.so-stock-image-gallery.open', openModal);
});
</script>

<style scoped>
.product-image-gallery {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.upload-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.file-input-hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.upload-button-label {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
  font-weight: 500;
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
  transition: all 0.2s;
  border: 1px solid var(--p-primary-color);
  background-color: var(--p-primary-color);
  color: var(--p-primary-contrast-color, #fff);
}

.upload-button-label:hover {
  filter: brightness(0.95);
}

.upload-button-label.is-disabled {
  opacity: 0.5;
  cursor: not-allowed;
  pointer-events: none;
}

.upload-progress {
  color: var(--p-text-muted-color, #666);
  font-size: 0.875rem;
  font-style: italic;
}

.state-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
  gap: 0.75rem;
  color: var(--p-text-muted-color, #888);
}

.state-block i {
  font-size: 2.5rem;
}

.empty-state p {
  margin: 0;
  font-size: 1rem;
}

.image-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
  gap: 1rem;
}

.image-card {
  border: 1px solid var(--p-surface-300, #ddd);
  border-radius: 8px;
  overflow: hidden;
  background: var(--p-surface-0, #fff);
  display: flex;
  flex-direction: column;
}

.image-card.is-primary {
  border-color: var(--p-warning-color, #f5a623);
  box-shadow: 0 0 0 1px var(--p-warning-color, #f5a623);
}

.image-thumb {
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  background: var(--p-surface-100, #f7f7f7);
  cursor: zoom-in;
  overflow: hidden;
}

.image-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.primary-badge {
  position: absolute;
  top: 0.5rem;
  left: 0.5rem;
  background: var(--p-warning-color, #f5a623);
  color: #fff;
  border-radius: 50%;
  width: 26px;
  height: 26px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.image-actions {
  display: flex;
  flex-direction: column;
  gap: 0.4rem;
  padding: 0.5rem;
}

.lightbox-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.85);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2rem;
}

.lightbox-overlay img {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 4px;
}

.lightbox-close-btn {
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  border: 2px solid #fff;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  font-size: 1.25rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}

.lightbox-close-btn:hover {
  background: rgba(0, 0, 0, 0.85);
}

@media (max-width: 768px) {
  .image-grid {
    grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  }
}
</style>
